/**
 * Count Mission sub-KPI type.
 *
 * Cumulative additive metric reaching toward a target. The data point's
 * `value` is the running cumulative count at that date — not a per-period
 * delta. Score reflects how close the count is to its target.
 *
 * Examples: students completed (cumulative across cohorts), trees planted
 * (cumulative through restoration phases), beneficiaries served, meals
 * distributed.
 *
 * Scoring math:
 *   score = round(min(100, (current_value / target_value) × 100))
 *
 * Trend uses raw value delta (not score delta) so that progress toward the
 * goal surfaces as +1 even when the score is pinned at 100 by deadline
 * degradation. This matches the existing mission.ts behavior for
 * `numeric` + `aggregation="cumulative"`.
 *
 * Deadline degradation is configurable per-instance via
 * `deadline_degradation_enabled` (default true). When enabled, an
 * unmet target degrades as the deadline approaches.
 *
 * See: DEV-814 KPI Engine Refinement, Phase 2.
 */

import type {
  ConfigFieldSchema,
  MissionDataPoint,
  MissionSubKpiType,
  SetupExample,
  SubKpiScore,
  UiHints,
  ValidationError,
} from './types';
import { latestAsOf } from '../timeSeries';
import { applyDeadlineDegradation } from './deadline';
import { KPI_DEFAULTS } from '../defaults';
import { validateCommonCrossFields } from './commonValidations';

/**
 * Configuration for a Count sub-KPI instance.
 */
export interface CountConfig extends Record<string, unknown> {
  /** Display name for the metric (e.g., "Students Completed"). */
  display_name: string;
  /** Target count to reach. */
  target_value: number;
  /** Unit of measure (e.g., "students", "trees", "meals"). */
  unit_of_measure: string;
  /** Optional deadline (YYYY-MM-DD) by which the target is expected. */
  deadline_date?: string;
  /** Days before deadline to trigger Yellow warning. Default 30. */
  warning_days?: number;
  /** Days before deadline to trigger Red critical. Default 7. */
  critical_days?: number;
  /** Whether deadline-proximity degradation is applied. Default true. */
  deadline_degradation_enabled?: boolean;
  /** Score >= this is green. Default 80. */
  green_threshold?: number;
  /** Score >= this (and < green_threshold) is yellow. Default 60. */
  yellow_threshold?: number;
}

/* ------------------------------------------------------------------------- */
/*  Scoring                                                                  */
/* ------------------------------------------------------------------------- */

function scoreCount(
  config: CountConfig,
  data: MissionDataPoint[],
  asOfDate: string,
): SubKpiScore {
  const { target_value, unit_of_measure } = config;

  const pts = data.map((d) => ({ date: d.date, value: d.value }));
  const cur = latestAsOf(pts, asOfDate);

  if (!cur) {
    return {
      score_0_100: null,
      progress_0_100: null,
      current_value: null,
      target_value,
      baseline_value: 0,
      explain: 'No count data available as of this date.',
    };
  }

  const currentValue = cur.value;

  // Defensive: zero or negative target makes the formula meaningless.
  if (target_value <= 0) {
    return {
      score_0_100: null,
      progress_0_100: null,
      current_value: currentValue,
      target_value,
      baseline_value: 0,
      explain: 'Target must be positive — score not computed.',
    };
  }

  // Base score: ratio of current to target, capped at 100.
  const ratio = currentValue / target_value;
  const baseScore = Math.round(Math.max(0, Math.min(100, ratio * 100)));
  const goalMet = currentValue >= target_value;

  // Apply deadline degradation (no-op if disabled or no deadline or goal met).
  const degraded = applyDeadlineDegradation({
    base_score: baseScore,
    goal_met: goalMet,
    asOfDate,
    config: {
      deadline_date: config.deadline_date,
      warning_days: config.warning_days,
      critical_days: config.critical_days,
      deadline_degradation_enabled: config.deadline_degradation_enabled,
    },
  });

  const baseExplain = goalMet
    ? `Target met: ${currentValue} of ${target_value} ${unit_of_measure}.`
    : `Counted ${currentValue} of ${target_value} ${unit_of_measure} (${baseScore}% of target).`;

  return {
    score_0_100: degraded.score,
    // DEV-864: Raw progress = (current/target) * 100 with NO deadline penalty.
    progress_0_100: baseScore,
    current_value: currentValue,
    target_value,
    baseline_value: 0,
    explain: degraded.explain ? `${degraded.explain} ${baseExplain}` : baseExplain,
  };
}

/* ------------------------------------------------------------------------- */
/*  Color derivation                                                         */
/* ------------------------------------------------------------------------- */

function colorFromScore(
  score: SubKpiScore,
  config: CountConfig,
): 0 | 1 | 2 | 3 {
  if (score.score_0_100 === null) return 0;
  const greenAt = config.green_threshold ?? KPI_DEFAULTS.mission.bands.green_threshold;
  const yellowAt = config.yellow_threshold ?? KPI_DEFAULTS.mission.bands.yellow_threshold;
  if (score.score_0_100 >= greenAt) return 3;
  if (score.score_0_100 >= yellowAt) return 2;
  return 1;
}

/* ------------------------------------------------------------------------- */
/*  Validation                                                               */
/* ------------------------------------------------------------------------- */

function validateConfig(config: CountConfig): ValidationError | null {
  // DEV-816 / Skip's bug report: per-field "is required" / "must be number"
  // checks were removed from this validator because they duplicate the
  // wizard's schema-driven per-field validation (`required: true` +
  // validateStep). Duplicating them here caused premature red banners on
  // Step 2 mount before the user had touched anything. Per-type
  // validateConfig now focuses on TRUE cross-field rules only.
  if (Number.isFinite(config.target_value) && config.target_value <= 0) {
    return { field: 'target_value', message: 'Target value must be positive.' };
  }
  return validateCommonCrossFields(config);
}

/* ------------------------------------------------------------------------- */
/*  Schema, examples, and UI hints                                           */
/* ------------------------------------------------------------------------- */

const config_schema: ConfigFieldSchema[] = [
  {
    key: 'display_name',
    label: 'What are you counting?',
    type: 'string',
    required: true,
    help_text:
      'A short name for the metric (e.g., "Students Completed", "Trees Planted").',
  },
  {
    key: 'target_value',
    label: 'Target count',
    type: 'number',
    required: true,
    help_text: 'The cumulative count to reach. Must be positive.',
    validation: { min: 0 },
    inline_with_next: true, // pair with unit_of_measure (DEV-816)
  },
  {
    key: 'unit_of_measure',
    label: 'Unit of measure',
    type: 'string',
    required: true,
    help_text: 'E.g., "students", "trees", "meals", "households".',
  },
  {
    key: 'tracking_start_date',
    label: 'Tracking start date (optional)',
    type: 'date',
    required: false,
    help_text: 'Scoring begins on this date. Reporting periods before this date are ignored.',
    inline_with_next: true, // pair with deadline_date (DEV-816)
  },
  {
    key: 'deadline_date',
    label: 'Target deadline (optional)',
    type: 'date',
    required: false,
    help_text: 'When the target should be reached. If set, score may degrade as deadline approaches.',
  },
  // deadline_degradation_enabled removed (DEV-814 / Skip): redundant once
  // color_mode is explicit. In 'score' mode, deadline always caps the score
  // when set. Users wanting raw progress with no deadline cap can pick
  // 'pace' mode (variance-driven) or 'deadline' mode (time-driven).
  // The color tolerance fields below are conditionally visible based on
  // color_mode (DEV-814). When color_mode='score' (default), the form shows
  // score-band thresholds. When color_mode='deadline', the form swaps in
  // days-from-deadline windows. Both sets of values persist independently —
  // switching modes doesn't lose the values from the other mode.
  {
    key: 'color_mode',
    label: 'Color the indicator from',
    type: 'enum',
    required: false,
    default_value: 'score',
    enum_options: [
      {
        value: 'score',
        label: 'Progress (% toward target)',
        help: 'Color is driven by current count as a percentage of target. Set the % thresholds below for green/yellow/red. Use this when the headline question is "How far along are we?"',
      },
      {
        value: 'deadline',
        label: 'Time remaining until deadline',
        help: 'Color is driven by days remaining: green far from deadline, yellow within the warning window, red within the critical window. Use this when the headline question is "How urgent is the deadline?"',
      },
      {
        value: 'pace',
        label: 'Pace (variance from expected by today)',
        help: 'Color is driven by how the actual count compares to a linear pace from tracking start to deadline. Green if on/ahead of pace, yellow if mildly behind, red if significantly behind. Use this when you want to flag drift early without waiting for a deadline window.',
        // DEV-816 / Skip: Pace mode does linear interpolation between
        // tracking_start_date and deadline_date — both are required for
        // the math to work. Disable the option until both are populated
        // so the user can't pick a mode that would silently produce
        // null scores.
        enabled_when: (c) =>
          !!(c.tracking_start_date && c.deadline_date),
        disabled_reason: 'Set both Tracking start date and Target deadline to enable Pace mode.',
      },
    ],
    // Fallback help shown when no option is selected (rare — the form has a default).
    help_text: 'Pick how the indicator color is computed.',
  },
  // Score-mode thresholds (visible when color_mode='score' or unset).
  //
  // Labels reframed for consistency with deadline/pace modes (DEV-814 / Skip):
  // all three color modes now express thresholds as "Yellow when X drops
  // below N" / "Red when X drops below M". The underlying field names stay
  // green_threshold/yellow_threshold (DB schema unchanged), but the user-
  // facing wording flips from "green at or above" to "yellow when drops
  // below". Mathematically identical:
  //
  //   "Green when % reached >= 80"     ↔  "Yellow when % drops below 80"
  //   "Yellow when % reached >= 60"    ↔  "Red when % drops below 60"
  //
  // Field name → user concept:
  //   green_threshold  → "Yellow drops below" (the floor of green = ceiling of yellow)
  //   yellow_threshold → "Red drops below"    (the floor of yellow = ceiling of red)
  {
    key: 'green_threshold',
    label: 'Yellow when % of target drops below',
    type: 'number',
    required: false,
    default_value: 80,
    suffix: '%',
    help_text: 'Indicator turns yellow once the % of target reached drops below this number. Default 80.',
    validation: { min: 0, max: 100 },
    visible_when: (c) => (c.color_mode ?? 'score') === 'score',
    inline_with_next: true, // pair with red threshold (DEV-816)
  },
  {
    key: 'yellow_threshold',
    label: 'Red when % of target drops below',
    type: 'number',
    required: false,
    default_value: 60,
    suffix: '%',
    help_text: 'Indicator turns red once the % of target reached drops below this number. Must be less than yellow. Default 60.',
    validation: { min: 0, max: 100 },
    visible_when: (c) => (c.color_mode ?? 'score') === 'score',
  },
  // Deadline-mode windows (visible when color_mode='deadline').
  // Labels reframed (DEV-816 / Skip) to match the "drops below" pattern of
  // score and pace: "Yellow when days until deadline drops below 30" reads
  // identically in shape to "Yellow when % of target drops below 80". The
  // ambiguous "within" wording is gone.
  {
    key: 'warning_days',
    label: 'Yellow when days until deadline drops below',
    type: 'number',
    required: false,
    default_value: 30,
    suffix: 'days',
    help_text: 'Indicator turns yellow once fewer than this many days remain until the deadline. Default 30.',
    validation: { min: 0 },
    visible_when: (c) => c.color_mode === 'deadline',
    inline_with_next: true, // pair with critical_days (DEV-816)
  },
  {
    key: 'critical_days',
    label: 'Red when days until deadline drops below',
    type: 'number',
    required: false,
    default_value: 7,
    suffix: 'days',
    help_text: 'Indicator turns red once fewer than this many days remain until the deadline. Must be less than yellow. Default 7.',
    validation: { min: 0 },
    visible_when: (c) => c.color_mode === 'deadline',
  },
  // Pace-mode tolerances (DEV-815). Visible only when color_mode='pace'.
  // Expected = (target / total_days) × days_elapsed. Variance = actual - expected.
  // Yellow when behind by >= yellow_units_behind; red when >= red_units_behind.
  // Labels reframed (DEV-816) to "[Color] when count falls behind expected
  // pace by" — explicit that the threshold is variance against a calculated
  // pace, not a static count.
  {
    key: 'pace_yellow_units_behind',
    label: 'Yellow when count falls behind expected pace by',
    type: 'number',
    required: false,
    default_value: 1,
    suffix: 'units',
    help_text: 'Indicator turns yellow once the actual count is at least this many units below the expected pace for today. Default 1.',
    validation: { min: 0 },
    visible_when: (c) => c.color_mode === 'pace',
    inline_with_next: true, // pair with red pace threshold (DEV-816)
  },
  {
    key: 'pace_red_units_behind',
    label: 'Red when count falls behind expected pace by',
    type: 'number',
    required: false,
    default_value: 2,
    suffix: 'units',
    help_text: 'Indicator turns red once the actual count is at least this many units below the expected pace for today. Must be greater than yellow. Default 2.',
    validation: { min: 0 },
    visible_when: (c) => c.color_mode === 'pace',
  },
];

const setup_examples: SetupExample[] = [
  {
    title: 'Students completed (Capacity Building)',
    description:
      'Cumulative count of students who completed the Wastewater Education program ' +
      'across all cohorts at this site, with a target of 100 by year-end.',
    example_config: {
      display_name: 'Students Completed',
      target_value: 100,
      unit_of_measure: 'students',
      deadline_date: '2026-12-31',
    },
  },
  {
    title: 'Trees planted (State Restoration)',
    description:
      'Cumulative count of trees planted across reforestation events, target 5,000 ' +
      'by the end of the planting season.',
    example_config: {
      display_name: 'Trees Planted',
      target_value: 5000,
      unit_of_measure: 'trees',
      deadline_date: '2027-04-30',
    },
  },
  {
    title: 'Meals distributed (long-running)',
    description:
      'Cumulative count of meals distributed via the food drive program, target ' +
      '50,000 across the program lifetime, no hard deadline.',
    example_config: {
      display_name: 'Meals Distributed',
      target_value: 50000,
      unit_of_measure: 'meals',
      deadline_degradation_enabled: false,
    },
  },
];

const ui_hints: UiHints = {
  default_chart: 'progress',
  value_format: 'count_with_unit',
  show_target_line: true,
};

/* ------------------------------------------------------------------------- */
/*  Export                                                                   */
/* ------------------------------------------------------------------------- */

export const countType: MissionSubKpiType<CountConfig> = {
  type_key: 'count',
  display_name: 'Count',
  description:
    'Track a cumulative number that should grow toward a target.',
  examples: [
    'students enrolled',
    'trees planted',
    'meals distributed',
  ],
  use_when: 'How many have we delivered?',
  supported_color_modes: ['score', 'deadline', 'pace'],
  goal_direction: 'increase',
  config_schema,
  validate_config: validateConfig,
  score: scoreCount,
  color_from_score: colorFromScore,
  ui_hints,
  setup_examples,
};
